import { forwardRef } from "react";
import { Box, TextField, type TextFieldProps } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import { RADIUS } from "../../../theme/mixins";

// ✅ Extend props to include mode
type StyledTextFieldProps = TextFieldProps & {
  mode?: "light" | "dark";
};

// White field, navy text, grey placeholder. Label is rendered ABOVE the field
// (design pattern) so it never overlaps or sits white-on-white inside the input.
const CustomTextField = styled(TextField, {
  shouldForwardProp: (prop) => prop !== "mode",
})<{ mode?: "light" | "dark" }>(({ theme, mode }) => ({
  borderRadius: RADIUS.input,
  "& .MuiOutlinedInput-root": {
    borderRadius: RADIUS.input,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    "& fieldset": {
      border:
        mode === "dark"
          ? "1px solid transparent"
          : `1px solid ${theme.palette.border.strong}`,
    },
    "&:hover fieldset": { borderColor: theme.palette.border.grey },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
      borderWidth: 1,
    },
    "& input": { color: theme.palette.text.primary },
    "& input::placeholder": {
      color: theme.palette.text.disabled,
      opacity: 1,
    },
  },
}));

const StyledTextField = forwardRef<HTMLInputElement, StyledTextFieldProps>(
  function StyledTextField({ mode = "dark", label, sx, ...props }, ref) {
    return (
      <Box sx={{ width: "100%" }}>
        {label !== undefined && label !== "" && (
          <Box
            component="label"
            sx={{
              display: "block",
              fontSize: "13px",
              fontWeight: 600,
              marginBottom: "6px",
              color: (theme) =>
                mode === "dark"
                  ? alpha(theme.palette.common.white, 0.82)
                  : theme.palette.text.primary,
            }}
          >
            {label}
          </Box>
        )}
        <CustomTextField
          {...props}
          inputRef={ref}
          mode={mode}
          fullWidth
          sx={sx}
        />
      </Box>
    );
  }
);

export default StyledTextField;
